<script setup lang="ts">
  import { nextTick, onBeforeUnmount, onMounted, ref, useSlots, watch } from 'vue';
  import { useI18n } from 'vue-i18n';
  import { components } from '@/cypaths';
  import { useFocusTrap } from '@vueuse/integrations/useFocusTrap';
  import IconButton from '@/components/common/formElements/buttons/IconButton.vue';
  import { useRouter, type RouterHistory, onBeforeRouteLeave, onBeforeRouteUpdate } from 'vue-router';
  import { MODAL_CONTENT } from '@/cypaths/cyComponents';
  import { A11Y_SCREEN_SIZE_200PCT_ZOOM } from '@/config';

  defineOptions({ name: 'ElModal' });

  const { open = false, closable = true, closeOnPopstate = true } = defineProps<{
    open?: boolean;
    closable?: boolean;
    /**
     * Ferme la modale à la réception d'un événement popState (browser back).  "true" par défaut.
     * Certains endroits comme dans le formulaire de réservation doit pouvoir gérer la modale de manière différente.
     */
    closeOnPopstate?: boolean;
  }>();

  const slots = useSlots();
  const modal = ref<HTMLDialogElement>();
  const modalForm = ref<HTMLFormElement>();
  const router = useRouter();
  const formClasses = ref<string>('');
  const bodyClasses = ref<string>('');

  /**
   * événements du routeur permettant de déterminer l'action de l'utilisateur ('back',
   * etc.) et ainsi prendre action en conséquence, le cas échéant
   */
  let navigationInfo = ref<Parameters<Parameters<RouterHistory['listen']>[0]>[2]>();

  router.options.history.listen((to, from, info) => {
    navigationInfo.value = info;
  });

  function routeChangeHandler(): boolean {
    if (!closeOnPopstate) {
      return true;
    }
    return navigationInfo.value?.type !== 'pop' || !open;
  }

  /** Action au changement de route */
  onBeforeRouteLeave(routeChangeHandler);

  /** Changement d'url sans pour autant changer de route (exemple: changement étape dans le formulaire, etc.) */
  onBeforeRouteUpdate(routeChangeHandler);

  /**
   * EX-693: Focus trap ajouté pour gérer le esc lorsque closable est à false.
   * Un tabindex à 0 a été ajouté sur le form pour permettre au focus trap de fonctionner et toujours avoir un
   * élément pour le focus.
   */
  const modalFocusTrap = useFocusTrap(modalForm);

  const { t } = useI18n();

  const emits = defineEmits<{
    close: [e: Event];
    /** Émis une fois le `<dialog>` réellement affiché (et le focus trap activé). */
    opened: [];
  }>();

  const showModal = () => {
    if (modal.value?.showModal) {
      modal.value.showModal();
      modalFocusTrap.activate();
      /**
       * À ce stade le `<dialog>` est affiché : ses descendants sont focusables. On notifie le parent afin qu'il
       * puisse déclencher l'autofocus d'un élément interne (ex. champ de recherche) sur un élément désormais
       * focusable. Le focus manuel s'exécute avant l'autofocus différé du focus-trap (`delayInitialFocus`), qui
       * conserve ensuite l'élément déjà focalisé puisqu'il appartient au trap.
       */
      emits('opened');
    }
  };

  const close = () => {
    if (modal.value?.close) {
      modalFocusTrap.deactivate();
      modal.value.close();
    }
  };

  /**
   * Événement natif lancé lors de la fermeture d'un dialog
   */
  const onDialogClose = (e: Event) => {
    emits('close', e);
    modalFocusTrap.deactivate();
  };

  /**
   * Méthode utilisée pour prévenir la fermeture du dialog avec la touche escape lorsque la propriété closable est à
   * false. Entre autre utilisée lorsque du contenu est en chargement, ex. lors de l'annulation d'un trip.
   *
   * Le `preventDefault` et le `stopPropagation` permettent de gérer la fermeture de dialogue dans un dialogue.
   *
   * Pour que l'annulation de l'événement `close` natif fonctionne, les conditions suivantes doivent être respectées :
   *   1. Le focus doit se trouver sur un enfant du dialog.
   *   2. Le dialog doit avoir `closable` à `false`.
   */
  const doCloseIfClosable = (e: KeyboardEvent) => {
    e.stopPropagation();
    e.preventDefault();
    if (!closable) {
      return;
    }
    close();
  };

  watch(
    () => open,
    async (newValue) => {
      if (newValue) {
        await nextTick();
        return showModal();
      }
      close();
    },
    { immediate: true },
  );
  const setFormClasses = () => {
    formClasses.value = document.body.offsetHeight >= A11Y_SCREEN_SIZE_200PCT_ZOOM
      ? 'grid overflow-hidden'
      : 'block overflow-auto';
    bodyClasses.value = document.body.offsetHeight >= A11Y_SCREEN_SIZE_200PCT_ZOOM
      ? 'overflow-auto h-full'
      : '';
  };

  onMounted(() => {
    if (closeOnPopstate) {
      window.addEventListener('popstate', onDialogClose);
    }
    window.addEventListener('resize', setFormClasses);
    setFormClasses();
  });

  onBeforeUnmount(() => {
    // Important pour le changement de page
    modalFocusTrap.deactivate();
    if (closeOnPopstate) {
      window.removeEventListener('popstate', onDialogClose);
    }
    window.removeEventListener('resize', setFormClasses);
  });

  defineExpose({ showModal });
</script>

<template>
  <dialog
    ref="modal"
    :data-cy="components.MODAL"
    class="el-modal group"
    @close="onDialogClose"
    @keydown.esc="doCloseIfClosable"
  >
    <form
      v-if="open"
      ref="modalForm"
      :data-cy="MODAL_CONTENT"
      method="dialog"
      class="size-full max-h-full md:h-auto md:max-h-[90vh]"
      :class="[slots.header || closable ? 'grid-rows-[min-content_1fr]' : 'grid-rows-[1fr] py-2', formClasses]"
      tabindex="0"
    >
      <template v-if="slots.header || closable">
        <div :class="!slots.header ? 'contents' : 'flex min-h-[5rem] flex-col pl-[clamp(var(--spacing-1),4%,var(--spacing-5))] pr-[clamp(2.5rem,68px,4.25rem)] py-[clamp(var(--spacing-1),5%,var(--spacing-6))] justify-center items-start border-b border-b-gray-light shadow-[0px_6px_10px_0px_rgba(0,0,0,0.15)] z-1'">
          <slot name="header" />
          <IconButton
            v-if="closable"
            class="el-modal__close absolute right-[clamp(var(--spacing-1),4%,var(--spacing-5))] top-[clamp(var(--spacing-1),5%,var(--spacing-6))] rounded-full bg-gray-light p-2 z-10"
            :class="{' md:top-6': !slots.header }"
            :label="t('generic.close.ariaLabel')"
            icon="close"
            size="small"
            :image-spacing="false"
            @click="doCloseIfClosable"
          />
        </div>
      </template>
      <div
        class="el-modal__body flex flex-col gap-4 has-[.el-loading-panel]:overflow-hidden"
        :class="bodyClasses"
      >
        <slot />
      </div>
    </form>
  </dialog>
</template>

<style scoped>
  @reference '../../../styles/index.css';

  .el-modal[open] {
    @apply fixed inset-0 h-auto max-h-dvh w-auto max-w-full bg-white md:h-fit md:max-h-[90vh] md:w-full md:max-w-[700px] md:rounded;
  }
  .el-modal[open]::backdrop {
    @apply bg-black opacity-70;
  }
</style>
